<template>
    <ValidationObserver ref="customerLeads">
        <div class="ls-customer-leads--container grid">
            <div class="grid__col-12">
                <slot></slot>
            </div>
            <div class="grid">
                <div class="grid__col-12">
                    <h2 class="ls-condensed">
                        What services are you interested in?
                    </h2>
                </div>
                <div class="grid__col-12">
                    <ValidationProvider v-slot="{ classes }" ref="services" rules="required">
                        <div class="ls-customer-leads--services-outer-container">
                            <div class="ls-customer-leads--services-inner-container ls-margin--top">
                                <a
                                    class="ls-button ls-button--block"
                                    @click.prevent="$router.go(-1)"
                                >
                                    Lawn Mowing
                                </a>
                            </div>
                            <div
                                v-for="service in showAll
                                    ? serviceOptions
                                    : serviceOptions.slice(0, 9)"
                                :key="service.id"
                                class="ls-customer-leads--services-inner-container ls-margin--top"
                            >
                                <label
                                    class="ls-button ls-button--block ls-button--checkbox"
                                    :class="{
                                        'ls-button--green ls-button--faded': services.includes(
                                            service.id
                                        ),
                                        ...classes,
                                    }"
                                >
                                    <input
                                        :id="service.id"
                                        v-model="services"
                                        v-ls-track.change="{
                                            target: 'ls_customer_leads-service',
                                            attachValue: true,
                                        }"
                                        type="checkbox"
                                        :value="service.id"
                                    />
                                    <span class="ls-button-toggle-icon">
                                        <i
                                            class="fa"
                                            :class="{
                                                'fa-check-square ls-color--green--color-dodge': services.includes(
                                                    service.id
                                                ),
                                                'fa-square-o ls-color--gray--light': !services.includes(
                                                    service.id
                                                ),
                                                ...classes,
                                            }"
                                        ></i>
                                    </span>
                                    <span>{{ service.name }}</span>
                                </label>
                            </div>
                        </div>
                    </ValidationProvider>
                    <a
                        v-if="!showAll"
                        v-ls-track.click="{
                            target: 'ls_customer_leads-view_complete_list',
                        }"
                        class="ls-customer-leads--show-all"
                        data-font-size="0"
                        @click.prevent="showAll = true"
                    >
                        <span>
                            View Complete List&nbsp;
                            <lsIconChevronRight />
                        </span>
                    </a>
                </div>
            </div>

            <div
                v-if="!order.flags['customer.signUp.landscapingLeads_V2']"
                class="grid"
                test-id="details"
            >
                <div class="ls-customer-leads--divider"></div>

                <div class="grid__col-12">
                    <h2 class="ls-condensed">
                        Additional Details
                    </h2>
                </div>

                <div class="grid__col-sm-10">
                    <label class="ls-label ls-margin--top" for="notes"
                        >Tell Us About the Job<span class="ls-normal">
                            (more details will help the pro give an immediate price)</span
                        ></label
                    >
                    <div class="ls-input__wrapper" :class="{ 'ls-padding--right': !screenSize.xs }">
                        <ValidationProvider v-slot="{ classes }" ref="notes" rules="required">
                            <textarea
                                id="notes"
                                v-model="notes"
                                v-ls-track.change="{
                                    target: 'ls_customer_leads-notes',
                                    attachValue: true,
                                }"
                                class="ls-input"
                                :class="classes"
                                type="textbox"
                                rows="3"
                                test-id="notes"
                            ></textarea>
                        </ValidationProvider>
                    </div>

                    <ValidationProvider v-slot="{ classes }" ref="locations" rules="required">
                        <label class="ls-label ls-margin--top" for="location">Location</label>
                        <div class="ls-customer-leads--locations-wrapper ls-input__wrapper">
                            <label
                                v-for="location in locationOptions"
                                :key="location.value"
                                class="ls-checkbox"
                                :class="classes"
                            >
                                <input
                                    :id="location.value"
                                    v-model="locations"
                                    type="checkbox"
                                    :value="location.value"
                                />
                                <i
                                    class="fa fa-lg fa-fw"
                                    :class="{
                                        'fa-check-square ls-color--green--color-dodge': locations.includes(
                                            location.value
                                        ),
                                        'fa-plus-square ls-color--gray--light': !locations.includes(
                                            location.value
                                        ),
                                    }"
                                ></i>
                                <span data-font-size="-1">{{ location.label }}</span>
                            </label>
                        </div>
                    </ValidationProvider>

                    <label class="ls-label ls-margin--top" for="budget">Budget</label>
                    <div
                        class="ls-input__wrapper for-select"
                        :class="{ 'ls-margin--right': !screenSize.xs }"
                    >
                        <ValidationProvider v-slot="{ classes }" ref="budget" rules="required">
                            <select
                                id="budget"
                                v-model="budget"
                                v-ls-track.change="{
                                    target: 'ls_customer_leads-budget',
                                    attachValue: true,
                                }"
                                class="ls-input"
                                :class="classes"
                                test-id="budget"
                            >
                                <option :value="null" selected disabled>- Select -</option>
                                <option
                                    v-for="budgetOption in budgetOptions"
                                    :key="budgetOption.label"
                                    :value="budgetOption.value"
                                >
                                    {{ budgetOption.label }}
                                </option>
                            </select>
                        </ValidationProvider>
                    </div>

                    <ValidationProvider v-slot="{ classes }" ref="stage" rules="required">
                        <LsRadios
                            v-model="stage"
                            label="Stage"
                            :options="stageOptions"
                            track-id="ls_customer_leads-stages"
                            :classes="classes"
                        />
                    </ValidationProvider>
                </div>
            </div>
        </div>
    </ValidationObserver>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import _ from 'lodash';
import { CUSTOMER_LEADS__SAVE_FORM_DATA } from '@/store/types';
import LsRadios from '@/components/LsRadios/LsRadios.vue';
import lsIconChevronRight from '@/components/general/icons/lsIconChevronRight.vue';

const locationOptions = [
    {
        label: 'Full yard',
        value: 'location_full',
    },
    {
        label: 'Front-yard',
        value: 'location_front',
    },
    {
        label: 'Backyard',
        value: 'location_back',
    },
    {
        label: 'Sideyard',
        value: 'location_side',
    },
    {
        label: 'Driveway',
        value: 'location_driveway',
    },
    {
        label: 'Other',
        value: 'location_other',
    },
];
export default {
    name: 'LsCustomerLeads',
    components: {
        LsRadios,
        lsIconChevronRight,
    },
    data() {
        return {
            // form
            serviceOptions: [],
            services: [],
            notes: '',
            locationOptions,
            locations: [],
            budgetOptions: [],
            budget: null,
            stageOptions: [],
            stage: null,
            showAll: false,
        };
    },
    computed: {
        ...mapState(['customerLeads', 'order', 'screenSize']),
    },
    created() {
        const {
            serviceOptions,
            services,
            notes,
            locations,
            budgetOptions,
            budget,
            stageOptions,
            stage,
            showAll,
        } = _.cloneDeep(this.customerLeads.form);
        this.serviceOptions = serviceOptions;
        this.services = services;
        this.notes = notes;
        this.locations = locations;
        this.budgetOptions = budgetOptions;
        this.budget = budget;
        this.stageOptions = stageOptions;
        this.stage = stage;
        this.showAll = showAll;
    },
    destroyed() {
        this.saveFormData();
    },
    methods: {
        ...mapActions([CUSTOMER_LEADS__SAVE_FORM_DATA]),
        saveFormData() {
            this[CUSTOMER_LEADS__SAVE_FORM_DATA]({
                services: this.services,
                notes: this.notes,
                locations: this.locations,
                budget: this.budget,
                stage: this.stage,
                showAll: this.showAll,
            });
        },
        async checkFormValidation() {
            return this.$refs.customerLeads.validate();
        },
    },
};
</script>

<style lang="scss">
.ls-customer-leads--container {
    .ls-input {
        resize: none;
    }

    .ls-customer-leads--services-outer-container {
        display: flex;
        flex-wrap: wrap;
    }

    .ls-customer-leads--services-inner-container {
        flex: 0 0 auto;
        padding-right: rem(0);
        width: 50%;
        @include max-screen(breakpoint(xs, max)) {
            padding-right: 0;
            width: 100%;
        }
    }

    .ls-customer-leads--invalid {
        color: palette(red) !important;
    }

    .ls-customer-leads--divider {
        background-color: #d5dbd9;
        height: 1px;
        margin: 16px 10px;
        width: 100%;
    }

    .ls-checkbox {
        display: flex;
        margin-left: 0;
        margin-bottom: rem(-3);
        padding-right: rem(-3);
        user-select: none;
        i {
            padding-right: rem(0);
            margin-top: 3px;
            font-size: rem(0);
        }
        span:before {
            // override spacing used elsewhere since we're using fa checkboxes
            content: '';
        }
        input {
            display: none;
        }
    }

    .ls-customer-leads--show-all {
        color: palette(green, color-dodge);
        margin: 15px 0 5px 0;
        font-weight: 600;
        > span {
            display: flex;
            justify-content: center;
            align-items: center;
            > svg {
                margin-left: 4px;
                width: 14px;
                height: 14px;
                > polygon {
                    fill: palette(green, color-dodge);
                }
            }
        }
    }

    .ls-customer-leads--locations-wrapper {
        display: flex;
        flex-flow: row wrap;
        > label {
            flex: 50%;
            max-width: 200px;
        }
    }
}
</style>
